import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Award, CheckCircle2 } from "lucide-react";
import { profile, certifications, aboutLogos, images } from "../mock/mock";

const journey = [
  {
    year: "Today",
    title: "Engineering Manager, IBM",
    copy: "Leading programs across AI, cloud, and cybersecurity — shipping reliable systems at enterprise scale.",
  },
  {
    year: "2023 → now",
    title: "GenAI Thought Leadership",
    copy: "Keynotes, publications and mentorship on integrating Generative AI into real enterprise workflows.",
  },
  {
    year: "Community",
    title: "Organizer · GDG Cloud Gandhinagar",
    copy: "Built and scaled a developer community recognized by Google for Developers.",
  },
  {
    year: "Always",
    title: "Mentor, speaker, builder",
    copy: "Working 1:1 with senior engineers and leaders navigating AI transformation and cybersecurity posture.",
  },
];

const values = [
  { t: "Clarity over cleverness", c: "Decisions should survive contact with reality. Simple beats clever." },
  { t: "Ship with security", c: "Speed and safety are not a trade-off — they are a design choice." },
  { t: "Teach to scale impact", c: "A teammate who learns to think is more valuable than a task you finish." },
  { t: "Measure or it did not happen", c: "Every initiative tied to outcome metrics. Hype expires. Numbers stay." },
];

export default function About() {
  return (
    <main className="bg-[var(--cream)] text-[var(--ink)]">
      {/* HERO — true 50/50 split */}
      <section className="relative grain border-b border-[var(--ink)]/20">
        <div className="grid grid-cols-1 md:grid-cols-2 min-h-[88vh]">
          {/* LEFT — full-bleed portrait */}
          <div className="relative bg-[var(--ink)] order-2 md:order-1 min-h-[70vh] md:min-h-0 overflow-hidden">
            <img
              src={profile.avatar}
              alt={profile.name}
              className="absolute inset-0 w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-[1200ms]"
            />
            <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[var(--ink)]/85 to-transparent pointer-events-none" />
            <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[var(--ink)]/55 to-transparent pointer-events-none" />

            <div className="absolute top-6 left-6 font-mono text-[0.62rem] uppercase tracking-[0.2em] text-[var(--cream)]/85">
              Portrait · 2026
            </div>
            <div className="absolute top-6 right-6 font-mono text-[0.62rem] uppercase tracking-[0.2em] text-[var(--cream)]/85">
              N° 002
            </div>

            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-6">
              <div>
                <div className="text-[var(--cream)]" style={{ fontFamily: "Inter", fontWeight: 700, fontSize: "1.6rem", letterSpacing: "-0.035em", lineHeight: 1 }}>
                  {profile.name}
                </div>
                <div className="font-mono text-[0.62rem] uppercase tracking-[0.2em] text-[var(--cream)]/70 mt-2">
                  {profile.currentRole}
                </div>
              </div>
              <div className="font-mono text-[0.62rem] uppercase tracking-[0.2em] text-[var(--cream)]/70 text-right hidden sm:block">
                Gujarat, IN
                <br />
                Remote worldwide
              </div>
            </div>
          </div>

          {/* RIGHT — content */}
          <div className="order-1 md:order-2 flex flex-col justify-between px-6 md:px-12 lg:px-16 py-14 md:py-16">
            <div className="flex items-center justify-between gap-3 tag-mono flex-wrap">
              <div className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-[var(--molten)]" />
                About
              </div>
              <div>Engineering · GenAI · Cyber</div>
            </div>

            <div className="py-10 md:py-14">
              <h1
                style={{
                  fontFamily: "Inter",
                  fontWeight: 800,
                  fontSize: "clamp(2.4rem, 4.8vw, 4.8rem)",
                  lineHeight: 0.96,
                  letterSpacing: "-0.045em",
                }}
              >
                Engineering leader.
                <br />
                <span className="italic-serif text-[var(--molten)]">Builder</span> of systems &amp; teams.
              </h1>

              <p
                className="mt-8 md:mt-10 max-w-xl leading-[1.4] text-[var(--ink)]"
                style={{ fontSize: "clamp(1rem, 1.3vw, 1.2rem)", fontWeight: 500, letterSpacing: "-0.01em" }}
              >
                Hi, I’m Dr. Drijesh. An Engineering Manager at IBM with{" "}
                <span className="italic-serif text-[var(--molten)]">15+ years</span> across cloud, AI, and cybersecurity.
              </p>

              <p className="mt-5 max-w-xl text-[var(--ink)]/65 leading-relaxed text-[0.95rem]">
                I help organisations move from AI experiments to engineered outcomes — and build the teams that make it repeatable.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <Link to="/awards" className="btn-ink">
                <span>See the work</span> <ArrowUpRight size={14} />
              </Link>
              <Link to="/contact" className="btn-outline">
                <span>Book a conversation</span> <ArrowUpRight size={13} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FACTS GRID — full width below hero */}
      <section className="grain">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 border-b border-[var(--ink)]/20">
            {[
              { k: "Role", v: "Engineering Manager", sub: "IBM" },
              { k: "Focus", v: "GenAI · Cyber · Cloud", sub: "Enterprise scale" },
              { k: "Experience", v: "15+ years", sub: "Engineering leadership" },
              { k: "Based", v: "Gujarat, IN", sub: "Remote worldwide" },
            ].map((f, i) => (
              <div
                key={f.k}
                className={`py-8 md:py-10 px-4 md:px-6 ${
                  i !== 3 ? "md:border-r border-[var(--ink)]/20" : ""
                } ${i < 2 ? "border-b md:border-b-0 border-[var(--ink)]/20" : ""}`}
              >
                <div className="tag-mono mb-3">{f.k}</div>
                <div className="font-serif text-2xl md:text-3xl" style={{ letterSpacing: "-0.03em", fontWeight: 700 }}>
                  {f.v}
                </div>
                <div className="mt-1 text-[var(--ink)]/55 text-sm">{f.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RECOGNITION STRIP */}
      <section className="section-ink grain py-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-12 gap-8 items-end mb-10">
            <div className="col-span-12 md:col-span-7">
              <div className="tag-mono mb-4">[ Recognition &amp; Impact ]</div>
              <h2 className="headline-lg text-[var(--cream)]">
                My work spans engineering, community, and <span className="italic-serif text-[var(--molten)]">AI enablement.</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-5 md:text-right">
              <Link to="/awards" className="btn-cream">Full awards <ArrowUpRight size={16} /></Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4 text-[var(--cream)]/85">
            <div className="flex gap-3"><Award size={18} className="mt-1 text-[var(--molten)] shrink-0" /><p>Awarded by Google for Developers for community leadership</p></div>
            <div className="flex gap-3"><Award size={18} className="mt-1 text-[var(--molten)] shrink-0" /><p>Led initiatives impacting large developer communities</p></div>
            <div className="flex gap-3"><Award size={18} className="mt-1 text-[var(--molten)] shrink-0" /><p>Speaker across conferences, institutions, and industry platforms</p></div>
            <div className="flex gap-3"><Award size={18} className="mt-1 text-[var(--molten)] shrink-0" /><p>Built programs enabling engineers to become industry-ready</p></div>
          </div>
        </div>
      </section>

      {/* JOURNEY */}
      <section className="py-24 md:py-32 grain">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="tag-mono mb-4">[ Journey ]</div>
          <h2 className="headline-lg mb-14 max-w-3xl">
            A non-linear path through cloud, AI, and <span className="italic-serif text-[var(--molten)]">cyber.</span>
          </h2>

          <div className="border-t border-[var(--ink)]/15">
            {journey.map((j, i) => (
              <div key={i} className="rise grid grid-cols-12 gap-6 py-8 border-b border-[var(--ink)]/15">
                <div className="col-span-12 md:col-span-3 tag-mono text-[var(--molten)]">{j.year}</div>
                <div className="col-span-12 md:col-span-9">
                  <div className="font-serif text-2xl md:text-3xl">{j.title}</div>
                  <p className="mt-3 text-[var(--ink)]/75 leading-relaxed max-w-2xl">{j.copy}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-24 md:py-32 bg-[var(--cream-soft)] grain">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-5">
              <div className="tag-mono mb-4">[ Operating principles ]</div>
              <h2 className="headline-lg max-w-md">
                How I <span className="italic-serif text-[var(--molten)]">actually</span> work.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {values.map((v, i) => (
                <div key={i} className="rise p-6 border border-[var(--ink)]/15 bg-[var(--cream)] hover:border-[var(--molten)] transition-colors">
                  <div className="font-serif text-xl md:text-2xl mb-2">{v.t}</div>
                  <p className="text-[var(--ink)]/70 leading-relaxed">{v.c}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CERTIFICATIONS */}
      <section className="py-24 md:py-32 grain">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-5">
            <div className="tag-mono mb-4">[ Certifications ]</div>
            <h2 className="headline-lg">
              Formally credentialed across <span className="italic-serif text-[var(--molten)]">the stack.</span>
            </h2>
            <p className="mt-6 text-[var(--ink)]/75 max-w-md leading-relaxed">
              Depth across program leadership, cloud architecture, GenAI, data engineering, and software craft.
            </p>
          </div>
          <div className="col-span-12 md:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {certifications.map((c, i) => (
                <div key={i} className="flex items-start gap-3 py-2 border-b border-[var(--ink)]/10">
                  <CheckCircle2 size={16} className="mt-1 text-[var(--molten)] shrink-0" />
                  <span className="text-[var(--ink)]/85">{c}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* LOGO GRID */}
      <section className="section-ink grain py-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="tag-mono mb-6">[ Worked with · spoken at · recognized by ]</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-0 border-t border-[var(--cream)]/20">
            {aboutLogos.map((l, i) => (
              <div
                key={i}
                className="py-6 px-4 border-b border-r border-[var(--cream)]/20 flex items-center"
              >
                <div className="font-serif text-base md:text-lg text-[var(--cream)]" style={{ fontWeight: 600, letterSpacing: "-0.02em" }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
